<template>
  <layout transition="scale-transition" :is-loading="isLoading">
    <template slot="header">
      Menu settings
    </template>

    <template>
      <v-form class="mb-4">
        <div class="px-4">
          <form-subheader>Standard items</form-subheader>
          <v-checkbox
            data-test-menu-checkbox="devices"
            class="pb-2"
            v-model="standardItems"
            label="Devices"
            value="devices"
            hide-details
          ></v-checkbox>
          <v-checkbox
            class="pb-2"
            v-model="standardItems"
            label="Datasets"
            value="datasets"
            data-test-menu-checkbox="datasets"
            hide-details
          ></v-checkbox>
          <v-checkbox
            class="pb-2"
            v-model="standardItems"
            label="Media"
            data-test-menu-checkbox="media"
            value="media"
            hide-details
          ></v-checkbox>
          <v-checkbox
            class="pb-2"
            v-model="standardItems"
            label="Applications"
            value="applications"
            data-test-menu-checkbox="applications"
            hide-details
          ></v-checkbox>
          <v-checkbox
            class="pb-2"
            v-model="standardItems"
            label="Users"
            value="users"
            data-test-menu-checkbox="users"
            hide-details
          ></v-checkbox>
          <v-checkbox
            class="pb-2"
            v-model="standardItems"
            label="Users Groups"
            value="users groups"
            data-test-menu-checkbox="usersGroup"
            hide-details
          ></v-checkbox>
          <v-checkbox
            class="pb-2"
            data-test-menu-checkbox="settings"
            v-model="standardItems"
            label="Settings"
            value="settings"
            hide-details
          ></v-checkbox>

          <form-subheader>Custom items</form-subheader>

          <div v-for="(item, index) in customItems" :key="item.id">
            <v-text-field
              :data-test-menu-custom="`name_${index}`"
              label="Item name"
              v-model="item.name"
              clearable
            ></v-text-field>
            <v-autocomplete
              hide-selected
              hide-no-data
              :data-test-menu-custom="`schemas_${index}`"
              :key="item.id"
              label="Schemas"
              v-model="item.schemas"
              :items="schemas"
              chips
              multiple
              item-text="name"
              item-value="id"
            >
              <template v-slot:item="{ item }">
                <div
                  :data-test-menu-custom-schema-value="`${item.name}_${index}`"
                >
                  {{ item.name }}
                </div>
              </template>
              <template v-slot:selection="data">
                <v-chip
                  small
                  color="primary"
                  close
                  @click:close="handleDropChip(data, item.schemas)"
                  close-icon="mdi-close-circle"
                >
                  {{ data.item.name }}
                </v-chip>
              </template>
            </v-autocomplete>
          </div>

          <v-btn
            elevation="0"
            data-test-menu-custom="add"
            small
            block
            @click="addCustomStub"
          >
            Add custom
          </v-btn>
        </div>
      </v-form>
    </template>
    <template slot="footer">
      <v-spacer />
      <v-btn
        text
        color="text-primary"
        data-test-menu-settings="cancel"
        @click.stop="close"
      >
        Cancel
      </v-btn>
      <v-btn
        text
        color="primary"
        data-test-menu-settings="save"
        depressed
        @click.stop="save"
      >
        Save
      </v-btn>
    </template>
  </layout>
</template>

<script>
import Layout from '@/layouts/LayoutPopup';
import { ref, onMounted } from '@vue/composition-api';
import useAuth from '@/api/auth';
import useObjects from '@/api/objects';
import { useLazyQuery, useResult } from '@vue/apollo-composable';
import schema from '@/api/schemas/list.graphql';
import { nanoid } from 'nanoid';
import cloneDeep from 'lodash.clonedeep';
import SelectChips from '@/components/SelectChips';

export default {
  name: 'MenuSettings',
  props: ['show'],
  components: { Layout, SelectChips },
  setup(props, context) {
    const { profile, loadProfileAsync } = useAuth();
    const isLoading = ref(false);

    const standardItemsId = ref('');
    const standardItems = ref([]);

    const customItemsId = ref('');
    const customItems = ref([
      {
        id: nanoid(),
        name: '',
        schemas: []
      }
    ]);

    const close = () => {
      context.emit('close');
    };

    const saveHandler = () => {
      close();
    };

    const { object } = useObjects();

    function save() {
      isLoading.value = true;
      let propertiesSend = [
        {
          value: standardItems.value,
          propertyId: standardItemsId.value
        },
        {
          value: customItems.value.filter(item => item.schemas.length > 0),
          propertyId: customItemsId.value
        }
      ];

      object
        .updateObjectPropertiesNoCache({
          propertiesArray: propertiesSend
        })
        .then(() => {
          return loadProfileAsync();
        })
        .then(() => {
          saveHandler();
        })
        .finally(() => {
          isLoading.value = false;
        });
    }

    const addCustomStub = () => {
      customItems.value.push({
        id: nanoid(),
        name: 'Item name',
        schemas: ''
      });
    };

    const lazyQuery = useLazyQuery(
      schema.loadPure,
      {
        filter: {
          parentSchemaExists: false
        }
      },
      {
        fetchPolicy: 'network-only'
      }
    );

    const schemas = useResult(lazyQuery.result, [], data => {
      return data.schemata;
    });

    onMounted(() => {
      lazyQuery.load();
      isLoading.value = true;
      loadProfileAsync()
        .then(() => {
          const standardItemsProp = cloneDeep(
            profile.value.objectProperties.find(
              item => item.key === 'menuSettingsStandardItems'
            )
          );

          const customItemsProp = cloneDeep(
            profile.value.objectProperties.find(
              item => item.key === 'menuSettingsCustomItems'
            )
          );

          if (standardItems) {
            standardItemsId.value = standardItemsProp.id;
            standardItems.value = standardItemsProp.value;
          }

          if (customItemsProp) {
            customItemsId.value = customItemsProp.id;

            if (customItemsProp.value.length === 0) {
              customItems.value = [
                ...customItems.value,
                ...customItemsProp.value
              ];
            } else {
              customItems.value = customItemsProp.value;
            }
          }
        })
        .finally(() => {
          isLoading.value = false;
        });
    });

    const handleDropChip = (data, array) => {
      array.splice(
        array.findIndex(i => i === data.item.id),
        1
      );
    };

    return {
      handleDropChip,
      close,
      saveHandler,
      save,
      isLoading,
      standardItems,
      schemas,
      loadingSchemas: lazyQuery.loading,
      customItems,
      addCustomStub
    };
  }
};
</script>
